import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, OnDestroy, SimpleChanges } from '@angular/core';

export interface SidesheetItem {
  icon?: string;
  title: string;
  subtitle?: string;
  items?: string[];
  price?: string;
  priceLabel?: string;
}

export type SidesheetType = 'benefits' | 'rating' | 'excesses' | 'help' | 'custom' | 'insured-items' | 'device-form' | 'item-form';

@Component({
    selector: 'app-sidesheet',
    templateUrl: './sidesheet.component.html',
    styleUrls: ['./sidesheet.component.scss'],
    standalone: false
})
export class SidesheetComponent implements AfterViewInit, OnDestroy {
  @ViewChild('sidesheetEl') sidesheetEl!: ElementRef<HTMLElement>;

  @Input() sidesheetId: string = 'sidesheet';
  @Input() title: string = '';
  @Input() type: SidesheetType = 'custom';
  @Input() data: SidesheetItem[] = [];
  @Input() showBackButton: boolean = true;
  @Input() showEditButton: boolean = false;
  @Input() showTray: boolean = false;
  @Input() trayTotal: string = '';
  @Input() ratingData: any = null;
  @Input() itemQuestions: any = null;
  @Input() editingIndex: number = -1;

  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() backClicked = new EventEmitter<void>();
  @Output() editClicked = new EventEmitter<void>();
  @Output() itemSelected = new EventEmitter<SidesheetItem>();
  @Output() excessChanged = new EventEmitter<any>();
  @Output() itemSave = new EventEmitter<void>();

  isOpen: boolean = false;
  activeExcessTab: number | null = null;
  loadingItemIds: Set<string> = new Set();
  invalidExcessItems: Set<string> = new Set();
  emailTouched = false;
  contactTouched = false;
  isItemFormValid = false;
  private readonly emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  private readonly contactRegex = /^(\+27|0)[1-8][0-9]{8}$/;
  private boundOutsideClickHandler: ((e: Event) => void) | null = null;

  ngAfterViewInit(): void {
    // Set up trigger listeners for elements with data-sidesheet-trigger attribute
    this.setupTriggers();
    
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.trayTotal?.currentValue !== changes?.trayTotal?.previousValue) {
      this.trayTotal = changes?.trayTotal?.currentValue;
    }
    // Parse ratingData if it arrives as a JSON string
    if (changes?.ratingData?.currentValue) {
      const raw = changes.ratingData.currentValue;
      if (typeof raw === 'string') {
        try { this.ratingData = JSON.parse(raw); } catch { this.ratingData = []; }
      }
    }
    // Set first tab as active when data changes for excesses type
    if (changes?.data?.currentValue && this.type === 'excesses') {
      const classes = this.getUniqueClasses(changes.data.currentValue);
      if (classes.length > 0 && !this.activeExcessTab) {
        this.activeExcessTab = classes[0].class_id;
      }
    }
  }
  ngOnDestroy(): void {
    this.removeOutsideClickListener();
  }

  private setupTriggers(): void {
    // Find all elements that trigger this sidesheet
    const triggers = document.querySelectorAll(`[data-sidesheet-trigger="${this.sidesheetId}"]`);
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e: Event) => {
        e.preventDefault();
        this.open(e);
      });
    });
  }

  open(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (!this.sidesheetEl?.nativeElement) {
      return;
    }

    this.sidesheetEl.nativeElement.classList.add('is-active');
    document.body.classList.add('sidesheet-open');
    this.isOpen = true;

    // Add outside click listener
    this.addOutsideClickListener();

    this.opened.emit();
  }

  close(): void {
    if (!this.sidesheetEl?.nativeElement) {
      return;
    }

    this.sidesheetEl.nativeElement.classList.remove('is-active');
    document.body.classList.remove('sidesheet-open');
    this.isOpen = false;
    this.emailTouched = false;
    this.contactTouched = false;

    this.removeOutsideClickListener();

    this.closed.emit();
  }

  onBackClick(event: Event): void {
    event.preventDefault();
    this.backClicked.emit();
    this.close();
  }

  onEditClick(event: Event): void {
    event.preventDefault();
    this.editClicked.emit();
  }

  onItemClick(item: SidesheetItem): void {
    this.itemSelected.emit(item);
  }

  // get isEmailValid(): boolean {
  //   const email = this.deviceFormData?.email;
  //   return !!email && this.emailRegex.test(email);
  // }

  // get isContactValid(): boolean {
  //   const contact = this.deviceFormData?.contact;
  //   return !!contact && this.contactRegex.test(contact.replace(/\s/g, ''));
  // }

  // get isDeviceFormValid(): boolean {
  //   const d = this.deviceFormData;
  //   return !!(d?.name && d?.surname && d?.id_number && d?.imei && this.isEmailValid && this.isContactValid);
  // }

  onEmailBlur(): void {
    this.emailTouched = true;
  }

  onContactBlur(): void {
    this.contactTouched = true;
  }

  onItemFormSave(): void {
    this.emailTouched = true;
    this.contactTouched = true;
    if (!this.isItemFormValid) { return; }
    this.itemSave.emit(this.itemQuestions);
    this.close();
    this.isItemFormValid = false;
  }

  // Helper methods for insured-items type
  getItemTitle(description: string): string {
    if (!description) return '';
    const parts = description.split('|');
    return parts[0] || '';
  }

  getItemSubtitle(description: string): string {
    if (!description) return '';
    const parts = description.split('|');
    return parts[1] || '';
  }

  parseJson(jsonString: string): any[] {
    if (!jsonString) return [];
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  }

  // Aggregated benefits row comes pre-built from getInsuredItems ("Incl. N benefits");
  // hidden when the count is 0
  getAggregatedBenefit(entry: any): any {
    const benefit = this.parseJson(entry.benefits)[0];
    if (!benefit) return null;
    const count = parseInt(String(benefit.benefits_label ?? '').match(/\d+/)?.[0] ?? '', 10);
    return count === 0 ? null : benefit;
  }

  // Subtotal tray helpers for insured-items type
  getSubtotalEntry(data: any[]): any {
    if (!data) return null;
    return (data as any[]).find((item: any) => item.entry_type === 'policy_premium') || null;
  }

  getFeeEntries(data: any[]): any[] {
    if (!data) return [];
    const feeTypes = ['fees', 'vat', 'sasria', 'addons', 'discount'];
    return (data as any[]).filter((item: any) => feeTypes.includes(item.entry_type));
  }

  buildTooltip(content: string): string {
    return `<div class="popover__content">${content}</div>`;
  }

  // Helper methods for excesses type
  getUniqueClasses(data: any[]): any[] {
    if (!data) return [];
    return data.filter(item => item.entry_type === 'class');
  }

  getItemsForClass(data: any[], classId: number): any[] {
    if (!data) return [];
    return data.filter(item => item.entry_type === 'item' && item.class_id === classId);
  }

  setActiveExcessTab(classId: number): void {
    this.activeExcessTab = classId;
  }

  onExcessInput(item: any, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseExcessValue(input.value);
    this.validateExcess(item, value);
  }

  onExcessChange(item: any, event: Event): void {
    const input = event.target as HTMLInputElement;
    const newValue = input.value;
    const numericValue = this.parseExcessValue(newValue);
    const itemKey = this.getItemKey(item);

    // Validate first
    if (!this.validateExcess(item, numericValue)) {
      return; // Don't proceed if invalid
    }

    // Show loading state
    this.loadingItemIds.add(itemKey);

    // Emit the change event with a callback to clear loading
    this.excessChanged.emit({
      item,
      newValue: numericValue,
      clearLoading: () => this.clearItemLoading(itemKey)
    });
  }

  // Validation helpers
  parseExcessValue(value: string): number {
    // Remove currency symbols, spaces, and commas
    const cleaned = value.replace(/[R\s,]/g, '');
    return parseFloat(cleaned) || 0;
  }

  validateExcess(item: any, value: number): boolean {
    const itemKey = this.getItemKey(item);
    const min = parseFloat(item.min_excess) || 0;
    const max = parseFloat(item.max_excess) || Infinity;

    if (value < min || value > max) {
      this.invalidExcessItems.add(itemKey);
      return false;
    } else {
      this.invalidExcessItems.delete(itemKey);
      return true;
    }
  }

  hasExcessError(item: any): boolean {
    return this.invalidExcessItems.has(this.getItemKey(item));
  }

  // Loading state helpers
  getItemKey(item: any): string {
    return `${item.class_id}-${item.item_id}`;
  }

  isItemLoading(item: any): boolean {
    return this.loadingItemIds.has(this.getItemKey(item));
  }

  setItemLoading(item: any): void {
    this.loadingItemIds.add(this.getItemKey(item));
  }

  clearItemLoading(itemKey: string): void {
    this.loadingItemIds.delete(itemKey);
  }

  clearAllLoading(): void {
    this.loadingItemIds.clear();
  }

  // Helper methods for rating type
  getStarsArray(): string[] {
    if (!this.ratingData?.[0]?.overall_rating) return [];
    return this.ratingData[0].overall_rating.split('|');
  }

  getStarPercentClass(percentValue: string): string {
    const percent = parseInt(percentValue, 10) || 0;
    if (percent === 100) {
      return 'stars__percent--100';
    } else if (percent === 75) {
      return 'stars__percent--75';
    } else if (percent === 50) {
      return 'stars__percent--50';
    } else if (percent === 25) {
      return 'stars__percent--25';
    }
    return '';
  }

  getStarsLabel(): string {
    const stars = this.getStarsArray();
    if (!stars.length) return '0 Stars';

    // Calculate rating based on percentages (100=1, 50=0.5, 0=0)
    const totalRating = stars.reduce((sum, val) => {
      const percent = parseInt(val, 10) || 0;
      return sum + (percent / 100);
    }, 0);

    return `${totalRating} ${totalRating === 1 ? 'Star' : 'Stars'}`;
  }

  getTrayFrequencyShort(): string {
    return (this.data as any[])?.find((i: any) => i.frequency_short)?.frequency_short ?? 'mo';
  }

  // Helper methods for benefits type
  getBenefitItems(data: any[]): any[] {
    if (!data) return [];
    return data.filter(item => item.entry_type === 'benefit');
  }

  getAddonsPremium(data: any[]): any {
    if (!data) return null;
    return data.find(item => item.entry_type === 'addons_premium') || null;
  }

  getBenefitsTitle(data: any[]): string {
    if (!data) return '';
    const header = data.find(item => item.entry_type === 'header');
    return header?.side_sheet_title || '';
  }

  private addOutsideClickListener(): void {
    this.boundOutsideClickHandler = (e: Event) => {
      const target = e.target as HTMLElement;

      // Check if click is on the overlay
      if (target.classList.contains('sidesheet__overlay')) {
        this.close();
        return;
      }

      // Check if click is outside the sidesheet inner content
      const sidesheetInner = this.sidesheetEl?.nativeElement.querySelector('.sidesheet__inner');
      if (sidesheetInner && !sidesheetInner.contains(target) && this.sidesheetEl.nativeElement.contains(target)) {
        this.close();
      }
    };

    // Delay adding the listener to prevent immediate closing
    setTimeout(() => {
      document.addEventListener('click', this.boundOutsideClickHandler!);
    }, 100);
  }

  private removeOutsideClickListener(): void {
    if (this.boundOutsideClickHandler) {
      document.removeEventListener('click', this.boundOutsideClickHandler);
      this.boundOutsideClickHandler = null;
    }
  }
  questionResponse(event: any): void {
    const question = this.itemQuestions?.find((q: any) => q.cent_aq_question_id === event?.cent_aq_question_id);
    if (question) {
      question.answer = event.answer;
      question.answer_id = event.answer_id;
    }
    this.isItemFormValid = this.itemQuestions?.every((q: any) => q.answer !== '' && q.answer !== null && q.answer !== undefined);
  }
}
